import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CSS } from '@dnd-kit/utilities'
import { styled } from 'baseui'
import { useSortable } from '@dnd-kit/sortable'
import { useDndContext } from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'
import { StyleObject } from 'styletron-react'
import Download from '../Icons/Download'
import { PLACEMENT, StatefulPopover } from 'baseui/popover'
import EditPageMenu from '../Pages/EditPageMenu/EditPageMenu'
import DownloadStateMenu from '../Pages/DownloadStateMenu/DownloadStateMenu'
import WithPopover, { WithPopoverHandle } from '../FloatingInspectorButton/WithPopover'
import DownloadPopover from '../FloatingInspectorButton/SharedComponents/Popover/DownloadPopover/DownloadPopover'
import ThumbnailCard, {
  ThumbnailBottomLeftBadgeWrapper,
  ThumbnailBottomRightBadgeWrapper,
  ThumbnailDownloadBadge,
  ThumbnailOrderBadge,
  ThumbnailMenuButton,
} from '@/components/ThumbnailCard'
import { SceneItemConfig, SceneSlot, ThumbnailItemData, scenesConfig } from './sceneItemConfig'
import { hexToRgba } from '@/scenes/engine/utils/utils'
import { lightTheme } from '@/customTheme'

interface SceneItemProps {
  item: ThumbnailItemData
  onSelect: (item: ThumbnailItemData, event: React.MouseEvent, isTargetTriggerPopover: boolean) => void
  onDownloadBadgeClick?: (item: ThumbnailItemData) => void
  isDownloadPopoverOpen?: boolean
  onDownloadPopoverOpenChange?: (itemId: string, isOpen: boolean) => void
  selected: boolean
  isContainerHovering?: boolean
  hoveringId?: string | null
  setHoveringId?: (hoveringId: string | null) => void
  openEditMenuId?: string | null
  setOpenEditMenuId?: (id: string | null) => void
  numberOfSelected?: number
  order: number
  isActive: boolean
  isDisabled?: boolean
  isLoading?: boolean
  /** Roving focus: only the list's tab stop is in the Tab order. */
  isTabStop?: boolean
  sceneItemConfig?: SceneItemConfig
  downloadDestination?: string
  downloadSource?: string
  onImageLoad?: React.ReactEventHandler<HTMLImageElement>
  onVideoLoad?: React.ReactEventHandler<HTMLVideoElement>
  /** Falls back to item.size when omitted. */
  baseSize?: {
    width: number
    height: number
  }
  /** Falls back to item.size when omitted. */
  activeSize?: {
    width: number
    height: number
  }
}

const ItemContainer = styled(
  'div',
  (props: { $selected: boolean; $isActive: boolean; $transform: string; $transition: string; style: StyleObject }) => ({
    transform: props.$transform,
    transition: props.$transition,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 2,
    borderRadius: '8px',
    outline: 'none',
    ':focus-visible': {
      outline: `2px solid ${lightTheme.colors.primary}`,
      outlineOffset: '2px',
    },
    ...props.style
  })
)

/** Enter / Space activate a focusable non-button element like a native button. */
const activateOnEnterOrSpace = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
  e.preventDefault()
  e.currentTarget.click()
}

const ThumbnailWrapper = styled('div', (props: {
  $baseWidth: number
  $baseHeight: number
  $activeWidth: number
  $activeHeight: number
  $isSelfHovering: boolean
}) => ({
  position: 'relative',
  height: props.$isSelfHovering ? `${props.$activeHeight}px` : `${props.$baseHeight}px`,
  width: props.$isSelfHovering ? `${props.$activeWidth}px` : `${props.$baseWidth}px`,
  willChange: 'height, width',
  transition: 'all 0.3s ease-out',
}))

const SceneItem: React.FC<SceneItemProps> = ({
  item,
  onSelect,
  onDownloadBadgeClick,
  isDownloadPopoverOpen = false,
  onDownloadPopoverOpenChange,
  selected,
  isContainerHovering = false,
  hoveringId = null,
  setHoveringId = () => {},
  openEditMenuId = null,
  setOpenEditMenuId = () => {},
  numberOfSelected = 1,
  order,
  isActive,
  isDisabled = false,
  isLoading = false,
  isTabStop,
  sceneItemConfig = scenesConfig,
  downloadDestination = '',
  downloadSource = '',
  onImageLoad,
  onVideoLoad,
  baseSize: baseSizeProp,
  activeSize: activeSizeProp,
}) => {
  const { t } = useTranslation()
  const FALLBACK_SIZE = { width: 64, height: 64 }
  const baseSize = baseSizeProp ?? item.size ?? FALLBACK_SIZE
  const activeSize = activeSizeProp ?? item.size ?? FALLBACK_SIZE
  const isDraggable = sceneItemConfig.draggable !== false && !isDisabled && !isLoading
  const { attributes, listeners, setNodeRef, transform, transition, active, isDragging } = useSortable({
    id: item.id,
    disabled: !isDraggable,
  })
  const [isSelfHovering, setIsSelfHovering] = useState(false)

  const transformString = CSS.Transform.toString(transform)

  const { active: activeContext } = useDndContext()
  const isAnyDragging = Boolean(activeContext)
  const isDraggedInMultiSelect = isAnyDragging && selected

  const [showDotsIcon, setShowDotsIcon] = useState(false)
  const isOpenEditMenu = openEditMenuId === item.id
  const downloadPopoverRef = useRef<WithPopoverHandle>(null)
  const isDownloadPopoverVisibleRef = useRef(false)
  
  const getSelfHover = useCallback(() => {
    if(hoveringId === 'add-scene') return false
    if(selected && hoveringId === null && isContainerHovering) return true
    if(openEditMenuId === item.id) return true
    if(isDownloadPopoverOpen) return true
    if(hoveringId === item.id) return true
    return false
  }, [hoveringId, openEditMenuId, item.id, isContainerHovering, isDownloadPopoverOpen, selected])

  useEffect(() => {
    setIsSelfHovering(() => getSelfHover())
  }, [getSelfHover])

  useEffect(() => {
    if (!downloadPopoverRef.current) {
      return
    }

    if (isDownloadPopoverOpen) {
      if (!isDownloadPopoverVisibleRef.current) {
        downloadPopoverRef.current.togglePopover()
      }
      return
    }

    if (isDownloadPopoverVisibleRef.current) {
      downloadPopoverRef.current.closePopover()
    }
  }, [isDownloadPopoverOpen])

  const isInteractionDisabled = isDisabled || isLoading

  // Slots render outside ItemContainer, so hovering one must keep it revealed.
  const [isSlotHovering, setIsSlotHovering] = useState(false)
  // Keyboard focus on the item or a slot reveals hover-only slots, so they are
  // visible (and reachable) while tabbing.
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false)
  const isRevealed = isSelfHovering || isSlotHovering || isKeyboardFocused

  const handleDownloadPopoverOpenChange = useCallback((isOpen: boolean) => {
    isDownloadPopoverVisibleRef.current = isOpen
    onDownloadPopoverOpenChange?.(item.id, isOpen)
  }, [onDownloadPopoverOpenChange, item.id])

  const handleClick = (e: React.MouseEvent) => {
    if (isInteractionDisabled) return
    const isTargetTriggerPopover =
      (e.target as HTMLElement).id === `trigger-popover-${item.id}` ||
      (e.target as HTMLElement).closest(`#trigger-popover-${item.id}`) !== null
    onSelect(item, e, isTargetTriggerPopover)
    if (!isTargetTriggerPopover) {
      setOpenEditMenuId(null)
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    if (isInteractionDisabled) return
    e.preventDefault()
    if (!selected) {
      onSelect(item, e, false)
    }
    const pageItem = document.getElementById(`trigger-popover-${item.id}`)
    if (pageItem) {
      pageItem.click()
    }
  }

  const handleMouseEnter = () => {
    if (isInteractionDisabled) return
    setShowDotsIcon(true)
    setIsSelfHovering(true)
    setHoveringId(item.id)
  }

  const handleMouseLeave = () => {
    if (isInteractionDisabled) return
    setShowDotsIcon(false)
    setIsSelfHovering(false)
    setHoveringId(null)
  }

  const getPopoverMenu = (close: () => void) => {
    if (typeof sceneItemConfig.popover === 'function') {
      return sceneItemConfig.popover(item, () => {
        close()
        setOpenEditMenuId(null)
        setShowDotsIcon(false)
      })
    }
    return sceneItemConfig.popover === 'stateMenu' ? (
      <DownloadStateMenu
        close={() => {
          close()
          setOpenEditMenuId(null)
          setShowDotsIcon(false)
        }}
        numberOfSelected={numberOfSelected}
      />
    ) : (
      <EditPageMenu
        close={() => {
          close()
          setOpenEditMenuId(null)
          setShowDotsIcon(false)
        }}
        source='page'
        numberOfSelected={numberOfSelected}
      />
    )
  }

  const renderPopover = () => sceneItemConfig.popover == null ? null : (
    <StatefulPopover
      content={({ close }) => getPopoverMenu(close)}
      triggerType="click"
      placement={PLACEMENT.bottomRight}
      popoverMargin={4}
      overrides={{
        Body: {
          style: () => ({
            zIndex: '10000',
            marginRight: '24px !important',
            marginTop: '-20px !important',
          }),
        },
      }}
      stateReducer={(a, b) => {
        setOpenEditMenuId(b.isOpen ? item.id : null)
        return b
      }}
      ignoreBoundary
    >
      <ThumbnailMenuButton
        $isOpen={isOpenEditMenu}
        $show={showDotsIcon}
        id={`trigger-popover-${item.id}`}
        onMouseEnter={() => setShowDotsIcon(true)}
      />
    </StatefulPopover>
  )

  const renderSlotElement = (element: Exclude<SceneSlot, { element: unknown }>) => {
    if (typeof element === 'function') return element(item, order)
    if (element === 'orderBadge') return <ThumbnailOrderBadge>{order}</ThumbnailOrderBadge>
    // `null` hides the download button for this item.
    const extraPopoverProps = sceneItemConfig.downloadPopoverProps ? sceneItemConfig.downloadPopoverProps(item) : {}
    if (extraPopoverProps === null) return null
    return (
      <WithPopover
        ref={downloadPopoverRef}
        onOpenChange={handleDownloadPopoverOpenChange}
        popoverContent={(buttonRef, closePopover) => (
          <DownloadPopover
            buttonRef={buttonRef}
            onClose={closePopover}
            placement="left"
            downloadScope={[item.id]}
            source={downloadSource}
            destination={downloadDestination}
            {...extraPopoverProps}
          />
        )}
      >
        <ThumbnailDownloadBadge
          $disabled={isInteractionDisabled}
          role="button"
          tabIndex={isInteractionDisabled ? -1 : 0}
          aria-label={t('Download')}
          aria-disabled={isInteractionDisabled}
          onKeyDown={activateOnEnterOrSpace}
          onClick={() => {
            onDownloadBadgeClick?.(item)
          }}
        >
          <Download size={18} fill={isInteractionDisabled ? hexToRgba(lightTheme.colors.blackGray, 0.5) : lightTheme.colors.blackGray} />
        </ThumbnailDownloadBadge>
      </WithPopover>
    )
  }

  const renderSlot = (slot: SceneSlot | undefined, Wrapper: typeof ThumbnailBottomLeftBadgeWrapper) => {
    if (slot == null) return null
    const { element, revealOnHover = false } = typeof slot === 'object' ? slot : { element: slot }
    const content = renderSlotElement(element)
    if (content == null) return null
    const hidden = revealOnHover && !isRevealed
    return (
      <Wrapper
        onMouseEnter={() => setIsSlotHovering(true)}
        onMouseLeave={() => setIsSlotHovering(false)}
        onFocus={() => setIsKeyboardFocused(true)}
        onBlur={() => setIsKeyboardFocused(false)}
        style={revealOnHover ? {
          opacity: hidden ? 0 : 1,
          visibility: hidden ? 'hidden' : 'visible',
          transition: 'opacity 120ms ease, visibility 120ms ease',
        } : undefined}
      >
        {content}
      </Wrapper>
    )
  }

  const showSlots = !isDragging && !isDraggedInMultiSelect && !isLoading

  return (
    <>
      <ItemContainer
        ref={setNodeRef}
        $selected={selected}
        $isActive={isActive}
        $transform={isDragging ? transformString : ''}
        $transition={isDragging ? transition || '' : ''}
        {...attributes}
        // dnd-kit marks a sortable it can't drag as disabled; the item is still selectable.
        aria-disabled={isDraggable ? attributes['aria-disabled'] : isInteractionDisabled}
        aria-label={sceneItemConfig.getItemLabel?.(item, order)}
        tabIndex={isTabStop === false ? -1 : attributes.tabIndex}
        data-scene-item-focus-target
        {...(isInteractionDisabled ? {} : listeners)}
        // No keyboard drag to start, so Enter / Space select like a native button.
        {...(isDraggable || isInteractionDisabled ? {} : { onKeyDown: activateOnEnterOrSpace })}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={(e) => {
          if (e.target === e.currentTarget) setIsKeyboardFocused(e.currentTarget.matches(':focus-visible'))
        }}
        onBlur={(e) => {
          if (e.target === e.currentTarget) setIsKeyboardFocused(false)
        }}
        style={{
          opacity: (selected && !isDragging && active) || isDragging ? 0 : 1,
          cursor: isInteractionDisabled ? 'default' : 'pointer',
          pointerEvents: isInteractionDisabled ? 'none' : 'auto',
        }}
      >
        <ThumbnailWrapper
          $baseWidth={baseSize.width}
          $baseHeight={baseSize.height}
          $activeWidth={activeSize.width}
          $activeHeight={activeSize.height}
          $isSelfHovering={isSelfHovering}
        >
          <ThumbnailCard
            imageSrc={item.preview}
            videoUrl={item.videoUrl}
            selected={selected}
            isActive={isActive}
            isLoading={isLoading}
            loadingOverlay={item.loadingOverlay}
            isDisabled={isDisabled}
            size={baseSize}
            renderContent={Boolean(item.preview || item.videoUrl || isLoading)}
            onImageLoad={onImageLoad}
            onVideoLoad={onVideoLoad}
            content={sceneItemConfig.renderPreview?.(item, baseSize)}
          >
            {!isDragging && !isInteractionDisabled && renderPopover()}
          </ThumbnailCard>
        </ThumbnailWrapper>
      </ItemContainer>
      {/* After the item so Tab reaches the thumbnail before its controls; z-index keeps them on top. */}
      {showSlots && renderSlot(sceneItemConfig.bottomLeftElement, ThumbnailBottomLeftBadgeWrapper)}
      {showSlots && renderSlot(sceneItemConfig.bottomRightElement, ThumbnailBottomRightBadgeWrapper)}
    </>
  )
}

export default SceneItem
